"use client";

import { useMemo } from "react";
import { settingsNavItems } from "@/components/settings/settings-nav-config";
import { useHasRootPriviliges } from "@/hooks/auth/use-has-root-priviliges";
import { useEntityStore } from "@/stores/entity.store";
import { useEntityPermissionsMap } from "@/hooks/schema/use-my-permissions";

export interface CommandBarPage {
  /** Stable, namespaced key (unique across settings + feature pages). */
  id: string;
  title: string;
  href: string;
}

/**
 * Top-level feature pages that live outside the settings area. Mirrors
 * `otherFeaturesNavItems` in app-sidebar.tsx — keep the two in sync.
 */
const featurePages: Array<CommandBarPage & { requiresEntity?: string }> = [
  { id: "feature-forms", title: "Forms", href: "/p/forms" },
  { id: "feature-flows", title: "Flows", href: "/p/flows" },
  { id: "feature-proposals", title: "Proposals", href: "/p/proposals" },
];

/**
 * The navigable settings + feature pages for the command bar, gated by the same
 * rules the sidebars use: non-root users only see Profile, and feature pages are
 * filtered on their required entity + read permission.
 */
export function useCommandBarPages(): CommandBarPage[] {
  const { hasRootPriviliges } = useHasRootPriviliges();
  const { getEntityByName } = useEntityStore();
  const { entityPermissionsMap } = useEntityPermissionsMap();

  return useMemo(() => {
    // Settings pages — non-root users only get Profile, matching SettingsSidebar.
    const settings: CommandBarPage[] = (
      hasRootPriviliges
        ? settingsNavItems
        : settingsNavItems.filter((item) => item.id === "profile")
    ).map((item) => ({ id: `settings-${item.id}`, title: item.title, href: item.href }));

    // Feature pages — gate on required entity + read permission.
    const features: CommandBarPage[] = featurePages
      .filter((page) => {
        if (!page.requiresEntity) return true;
        const entity = getEntityByName(page.requiresEntity);
        if (!entity) return false;
        return entityPermissionsMap?.get(entity.id)?.has_read ?? false;
      })
      .map(({ id, title, href }) => ({ id, title, href }));

    return [...features, ...settings];
  }, [hasRootPriviliges, getEntityByName, entityPermissionsMap]);
}
